import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import Header from '@/components/layout/Header';
import { showSuccess, showError, confirmAction, confirmDelete } from '@/lib/swal';
import { Download, Trash2, FileText } from 'lucide-react';

export default function MyDataPage() {
  const { currentUser, userProfile, logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const handleExportData = async () => {
    setLoading(true);
    try {
      // In real implementation, this would call a Cloud Function to generate the export
      showSuccess('Your data export has been generated. Download will start shortly.');
      
      // Simulate download
      const data = {
        profile: userProfile,
        exportDate: new Date().toISOString(),
      };
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `dining-steward-data-${Date.now()}.json`;
      a.click();
    } catch (error) {
      console.error('Error exporting data:', error);
      showError('Failed to export data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    const result = await confirmDelete('your account');
    
    if (result.isConfirmed) {
      const confirmResult = await confirmAction({
        title: 'Final Confirmation',
        text: 'This action cannot be undone. All your personal data will be permanently deleted.',
        confirmButtonText: 'Delete My Account',
        cancelButtonText: 'Keep My Account',
      });

      if (confirmResult.isConfirmed) {
        setLoading(true);
        try {
          // In real implementation, this would:
          // 1. Call Cloud Function to anonymize/delete data
          // 2. Delete Firebase Auth account
          // 3. Log out user
          showSuccess('Your account has been scheduled for deletion.');
          await logout();
          navigate('/');
        } catch (error) {
          console.error('Error deleting account:', error);
          showError('Failed to delete account. Please contact support.');
        } finally {
          setLoading(false);
        }
      }
    }
  };

  return (
    <div className="flex min-h-screen flex-col">
      <Header />
      <main className="flex-1 px-4 py-8">
        <div className="container mx-auto max-w-4xl">
          <div className="mb-8">
            <Button variant="ghost" onClick={() => navigate('/dashboard')}>
              ← Back to Dashboard
            </Button>
          </div>

          <h1 className="mb-8 text-3xl font-bold">My Data & Privacy</h1>

          {/* Account Information */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Account Information</CardTitle>
              <CardDescription>Your personal information stored on Dining Steward</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <div className="text-sm font-semibold">Name</div>
                <div className="text-muted-foreground">{userProfile?.name}</div>
              </div>
              <div>
                <div className="text-sm font-semibold">Email</div>
                <div className="text-muted-foreground">{userProfile?.email}</div>
              </div>
              <div>
                <div className="text-sm font-semibold">Unit Memberships</div>
                <div className="text-muted-foreground">
                  {(userProfile?.units || userProfile?.units)?.length || 0} unit(s)
                </div>
              </div>
              <div>
                <div className="text-sm font-semibold">Account Created</div>
                <div className="text-muted-foreground">
                  {userProfile?.createdAt.toLocaleDateString()}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* GDPR Rights */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Your Rights (GDPR)</CardTitle>
              <CardDescription>
                Under GDPR, you have rights regarding your personal data
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-start space-x-3">
                <FileText className="mt-1 h-5 w-5 text-primary" />
                <div>
                  <h3 className="font-semibold">Right to Access</h3>
                  <p className="text-sm text-muted-foreground">
                    You can request a copy of all your personal data we hold.
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <FileText className="mt-1 h-5 w-5 text-primary" />
                <div>
                  <h3 className="font-semibold">Right to Rectification</h3>
                  <p className="text-sm text-muted-foreground">
                    You can update your profile information at any time.
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <FileText className="mt-1 h-5 w-5 text-primary" />
                <div>
                  <h3 className="font-semibold">Right to Erasure</h3>
                  <p className="text-sm text-muted-foreground">
                    You can request deletion of your account and personal data.
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <FileText className="mt-1 h-5 w-5 text-primary" />
                <div>
                  <h3 className="font-semibold">Right to Data Portability</h3>
                  <p className="text-sm text-muted-foreground">
                    You can export your data in a machine-readable format.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Actions */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Data Actions</CardTitle>
              <CardDescription>Manage your personal data</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold">Export Your Data</h3>
                  <p className="text-sm text-muted-foreground">
                    Download a JSON file containing all your personal data
                  </p>
                </div>
                <Button onClick={handleExportData} disabled={loading}>
                  <Download className="mr-2 h-4 w-4" />
                  Export Data
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Danger Zone */}
          <Card className="border-destructive">
            <CardHeader>
              <CardTitle className="text-destructive">Danger Zone</CardTitle>
              <CardDescription>Irreversible actions</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold">Delete Account</h3>
                  <p className="text-sm text-muted-foreground">
                    Permanently delete your account and all associated data
                  </p>
                </div>
                <Button variant="destructive" onClick={handleDeleteAccount} disabled={loading}>
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete Account
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Additional Information */}
          <div className="mt-8 rounded-lg bg-muted p-4 text-sm">
            <p className="font-semibold mb-2">Need Help?</p>
            <p className="text-muted-foreground">
              For any data privacy questions or concerns, please contact us at{' '}
              <a href="mailto:privacy@masonic-tools.co.uk" className="text-primary underline">
                privacy@masonic-tools.co.uk
              </a>
              . We will respond within 72 hours as required by GDPR.
            </p>
          </div>
        </div>
      </main>
    </div>
  );
}

